/**
 * RelationshipStatusLine — a one-line, human relationship summary shown at the
 * top of the record Overview (Lightfield-style "Account summary" lead-in).
 *
 * Resolves the most recent genuine *interaction* — emails, LinkedIn messages,
 * and web form submissions. It deliberately EXCLUDES internal CRM events
 * (object creation, property updates, association changes, system/errors):
 * those aren't interactions, and counting them made a freshly-imported contact
 * read "Last interaction 2 months ago" even though it had never been emailed
 * or met (contradicting the "Last emailed: Never" / "Touchpoints: 0" tiles).
 *   - has interactions → "Last interaction 3 days ago"
 *   - none             → "No recorded interactions with {Name}"
 *
 * Supplementary by design: renders nothing on error and a single skeleton line
 * while loading to avoid layout shift.
 */

import { Skeleton } from '@/lib/base/skeleton/skeleton';

import type { CrmObject } from '../../core/types';
import { useHighlightLastEvent } from '../highlights/useHighlightLastEvent';
import type { HighlightDefinition } from '../registry/entityTypeRegistry';
import { EVENT_TYPES } from '../timeline/types';

// Genuine outward/inward touchpoints with the person — NOT internal CRM record
// edits. Passive signals (email opens, page views) and system events are
// excluded so the summary matches the at-a-glance interaction tiles.
const INTERACTION_DEFINITION: Extract<
  HighlightDefinition,
  { kind: 'last_event' }
> = {
  kind: 'last_event',
  label: 'Last interaction',
  eventTypeNames: [
    EVENT_TYPES.EMAIL_SENT,
    EVENT_TYPES.EMAIL_RECEIVED,
    EVENT_TYPES.LINKEDIN_MESSAGE_SENT,
    EVENT_TYPES.LINKEDIN_MESSAGE_RECEIVED,
    EVENT_TYPES.WEB_FORM_SUBMITTED,
  ],
};

interface RelationshipStatusLineProps {
  object: CrmObject;
  /** Display name used in the empty-state copy. */
  displayName: string;
}

export function RelationshipStatusLine({
  object,
  displayName,
}: RelationshipStatusLineProps) {
  const { value, isLoading, isError } = useHighlightLastEvent(
    object.org_id,
    object,
    INTERACTION_DEFINITION
  );

  // Status line is supplementary — stay silent on error rather than show a
  // broken/misleading line.
  if (isError) {
    return null;
  }

  if (isLoading) {
    return <Skeleton width={200} height='1rem' />;
  }

  const text = value
    ? `Last interaction ${value}`
    : `No recorded interactions with ${displayName}`;

  return <p className='text-body-small text-tertiary'>{text}</p>;
}
